// @ts-strict-ignore
import Link from 'next/link';
import { slugify } from 'underscore.string';
import { useDispatch, useSelector } from 'src/store/store';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import i18n from 'src/utils/translate';
import UserLink from 'src/modules/shared/links/userLink';
import Badge from 'src/components/badge';
import RelativeTime from 'src/components/relativeTime';
import { Size } from 'src/components/avatar';
import Separator from 'src/components/separator';
import { ResultsPost, ResultsPostTitle, ResultsPostFooter, ResultsPostFooterRow, ResultsPostFooterLinkWrapper, ResultsPostFooterReplies } from 'styles-js/resultsList';
import { Author } from 'src/constants/types';
export type PollItemType = {
  author: Author;
  postId: number;
  dateCreated: string;
  totalVotes: number;
  title: string;
};
type Props = {
  poll: PollItemType;
};
export default function PollItem({
  poll: {
    author,
    postId,
    dateCreated,
    totalVotes,
    title
  }
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const {
    currentCommunity,
    currentUser
  } = useSelector(({
    context
  }) => context);
  const pollLink = `/${currentCommunity.slug}/polls/${postId}/${slugify(title)}`;
  const pollLinkHref = `/community/poll?communitySlug=${currentCommunity.slug}&id=${postId}&slug=${slugify(title)}`;
  const isVisitorNoVotesLink = !currentUser && totalVotes === 0;
  // If title only has non-ASCII characters (!@£, emoji, etc), slugify(title) will return null and next/router will complain about an invalid href
  // because of repeated forward-slashes (//). We avoid '{poll-id}//result' in URL with the next line.
  const resultPart = slugify(title) ? '/result' : 'result';
  const votesLink = `${pollLink}${totalVotes > 0 ? resultPart : ''}`;
  const votesLinkHref = `${pollLinkHref}${totalVotes > 0 ? '&result=result' : ''}`;
  const onPollClick = section => {
    trackFootfallEvent(Event.Clicked, {
      clickType: 'polls-list',
      clickMetadata: {
        'poll-id': postId,
        'link-type': section
      }
    });
  };
  const onUserClick = section => {
    trackFootfallEvent(Event.Clicked, {
      clickType: 'polls-list',
      clickMetadata: {
        'poll-id': postId,
        'link-type': section
      }
    });
  };
  return <ResultsPost data-sentry-element="ResultsPost" data-sentry-component="PollItem" data-sentry-source-file="pollItem.tsx">
      <Link as={pollLink} href={pollLinkHref} onClick={() => onPollClick('title')} data-sentry-element="Link" data-sentry-source-file="pollItem.tsx">
        <ResultsPostTitle data-sentry-element="ResultsPostTitle" data-sentry-source-file="pollItem.tsx">{title}</ResultsPostTitle>
      </Link>
      <ResultsPostFooter data-sentry-element="ResultsPostFooter" data-sentry-source-file="pollItem.tsx">
        <UserLink avatarSize={Size.Small} signupContext={{
        section: Section.CommunityPostsPoll,
        element: Element.Avatar
      }} user={author} onClick={() => onUserClick('avatar')} data-sentry-element="UserLink" data-sentry-source-file="pollItem.tsx" />
        <div>
          <ResultsPostFooterRow data-sentry-element="ResultsPostFooterRow" data-sentry-source-file="pollItem.tsx">
            <ResultsPostFooterLinkWrapper data-sentry-element="ResultsPostFooterLinkWrapper" data-sentry-source-file="pollItem.tsx">
              <UserLink signupContext={{
              section: Section.CommunityPostsPoll,
              element: Element.UserLink
            }} user={author} onClick={() => onUserClick('user')} data-sentry-element="UserLink" data-sentry-source-file="pollItem.tsx" />
            </ResultsPostFooterLinkWrapper>
            {author?.roles?.length > 0 && <Badge>{author.roles[0]}</Badge>}
            <Separator className="hidden-xxs" data-sentry-element="Separator" data-sentry-source-file="pollItem.tsx" />
            <RelativeTime rawTime={dateCreated} data-sentry-element="RelativeTime" data-sentry-source-file="pollItem.tsx" />
          </ResultsPostFooterRow>
          <ResultsPostFooterRow data-sentry-element="ResultsPostFooterRow" data-sentry-source-file="pollItem.tsx">
            <Link as={votesLink} href={votesLinkHref} onClick={() => {
            if (isVisitorNoVotesLink) {
              dispatch(showSignupModal());
            } else {
              onPollClick('votes');
            }
          }} data-sentry-element="Link" data-sentry-source-file="pollItem.tsx">
              {totalVotes > 0 ? <ResultsPostFooterReplies>
                  {totalVotes} {totalVotes === 1 ? i18n.t('Voter') : i18n.t('Voters')}
                </ResultsPostFooterReplies> : <>{!currentCommunity.isArchived && i18n.t('Be the first to vote')}</>}
            </Link>
          </ResultsPostFooterRow>
        </div>
      </ResultsPostFooter>
    </ResultsPost>;
}